import { fetchJsonApi, normalizeTiyinPrice, toApiError } from "@/shared/api/common";
import { http } from "@/shared/api/http";

export type Medicine = {
  id: string | number;
  group_id?: number | null;
  name: string;
  trade_name?: string | null;
  international_name_ru?: string | null;
  international_name?: string | null;
  vendor_name?: string | null;
  dosage_form?: string | null;
  product_type?: string | null;
  strength?: string | null;
  volume?: string | null;
  container_type?: string | null;
  pack?: number | null;
  atc_code?: string | null;
  vendor_id?: number | null;
  min_price?: number | null;
  max_price?: number | null;
  wholesale_price?: number | null;
  wholesale_price_min?: number | null;
  wholesale_price_max?: number | null;
  vendor_country?: string | null;
  price?: number | null;
  minimal_price?: number | null;
  offer_count?: number | null;
  offer_ids?: number[] | null;
  lowest_offer_price?: number | null;
  lowest_offer_id?: number | null;
  lowest_promo_price?: number | null;
  is_prescription?: boolean | null;
  last_update?: string | null;
  prices_crawled_at?: string | null;
  price_updated_date?: string | null;
  score?: number;
  highlights?: Partial<Record<string, string>> | null;
};

export type MedicinePricePoint = {
  date: string;
  avgPrice: number;
  minPrice: number;
  maxPrice: number;
  samples: number;
};

export type MedicinePriceChanges = {
  medicine: Medicine;
  period: { key: string; days: number };
  summary: {
    startPrice: number | null;
    endPrice: number | null;
    change: number | null;
    changePercent: number | null;
    minPrice: number | null;
    maxPrice: number | null;
    samples: number;
  };
  series: MedicinePricePoint[];
};

export type MedicineFindParams = {
  name: string;
  vendorIds?: number[];
  region?: string | null;
  dosageForm?: string;
  strength?: string;
  volume?: string;
  withOfferCounts?: boolean;
  withLowestPrices?: boolean;
};

export type MedicineFindResponse = {
  data: Medicine[];
};

export type MedicineRecommendationRequest = {
  query: string;
  limit?: number;
  region?: string | null;
  age_years?: number | null;
  sex?: string | null;
  pregnancy_lactation?: string | null;
  prescription?: string | null;
  group_variants?: boolean;
  with_availability?: boolean;
};

export type MedicineRecommendationMatchSource = "medicine_detail" | "icd_atc" | "atc" | string;

export type MedicineRecommendationWarning = {
  type?: string | null;
  code?: string | null;
  message?: string | null;
  text?: string | null;
  field?: string | null;
  severity?: string | null;
  [key: string]: unknown;
};

export type MedicineRecommendationEvidence = {
  source?: string | null;
  field?: string | null;
  text?: string | null;
  [key: string]: unknown;
};

export type MedicineRecommendation = Partial<Medicine> & {
  medicine_id?: string | number | null;
  catalog_id?: string | number | null;
  score?: number | null;
  confidence?: number | null;
  match_score?: number | null;
  match_sources?: MedicineRecommendationMatchSource[] | null;
  sources?: MedicineRecommendationMatchSource[] | null;
  evidence?: Array<string | MedicineRecommendationEvidence> | null;
  snippets?: Array<string | MedicineRecommendationEvidence> | null;
  warnings?: MedicineRecommendationWarning[] | string[] | null;
  vendor?: {
    id?: string | number | null;
    name?: string | null;
    country_name?: string | null;
    logo_sm?: string | null;
    logo_big?: string | null;
    [key: string]: unknown;
  } | null;
  availability?: {
    offer_count?: number | null;
    lowest_offer_price?: number | null;
    [key: string]: unknown;
  } | null;
  variants?: Array<Partial<Medicine> & { id?: string | number | null }> | null;
  grouped_ids?: Array<string | number> | null;
  variant_ids?: Array<string | number> | null;
  variant_medicine_ids?: Array<string | number> | null;
  [key: string]: unknown;
};

export type MedicineRecommendationPlanner = {
  [key: string]: unknown;
};

export type MedicineRecommendationResponse = {
  data: {
    recommendations?: MedicineRecommendation[];
    planner?: MedicineRecommendationPlanner | null;
    disclaimer?: string | null;
    total_candidates?: number | null;
  };
};

type DailyPricesResponse = {
  dates?: string[];
  avg?: number[];
  min?: number[];
  max?: number[];
  offers?: number[];
};

type MedicinePriceChangesFallback = Partial<Medicine> & Pick<Medicine, "id" | "name">;

export type ColumnarPriceHistory = {
  dates?: string[];
  avg?: number[];
  median?: number[];
  min?: number[];
  max?: number[];
  stddev?: number[];
  offers?: number[];
  min_org?: number[];
  max_org?: number[];
};

export type ExpiryChangesHistory = {
  dates?: string[];
  changes?: number[];
  total_orgs?: number[];
  turnover_rate?: number[];
};

export type LatestWholesaleOfferItem = {
  id: string;
  medicine_id: string;
  organization_id: string;
  organization?: {
    id: string;
    name?: string | null;
    slug?: string | null;
  } | null;
  raw_name?: string | null;
  vendor_name?: string | null;
  vendor_id?: string | null;
  price: number;
  promo_price?: number | null;
  promotion_id?: string | null;
  promotion_text?: string | null;
  promotion_type?: string | null;
  buy_quantity?: number | null;
  bonus_quantity?: number | null;
  bonus_medicine_id?: string | null;
  bonus_medicine_raw_name?: string | null;
  promo_start_date?: string | null;
  promo_end_date?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  batch_first_seen_date?: string | null;
  batch_age_days?: number | null;
  batch_age_lower_bound?: boolean | null;
  payment_percentage?: number | null;
  cheapest_medicine_id_offer_id?: number | null;
  most_expensive_medicine_id_offer_id?: number | null;
  is_cheapest_by_medicine_id?: boolean | null;
  is_cheapest_by_trade_name?: boolean | null;
  is_most_expensive_by_medicine_id?: boolean | null;
  price_list_file_id?: string | null;
  row_index?: number | null;
  expiry_unchanged_days?: number | null;
  expiry_unchanged_approx?: boolean | null;
};

export type LatestWholesaleOffersResponse = {
  items?: LatestWholesaleOfferItem[];
  organizations?: Array<{
    organization_id: string;
    name?: string | null;
  }>;
  not_found?: Array<string | number>;
  total_price?: number;
};

const PERIOD_TO_DAYS: Record<string, number> = {
  "7d": 7,
  "14d": 14,
  "30d": 30,
  "90d": 90,
  week: 7,
  month: 30,
  year: 365,
};

function parsePeriod(period: string) {
  const days = PERIOD_TO_DAYS[period] ?? PERIOD_TO_DAYS["30d"];
  const key = PERIOD_TO_DAYS[period] ? period : "30d";

  return { key, days };
}

function toTiyin(value: number | null | undefined) {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(value * 100) : null;
}

function roundToTwo(value: number) {
  return Math.round(value * 100) / 100;
}

function buildMedicinePriceChangesFromDailyPrices(
  medicineId: string | number,
  dailyPrices: DailyPricesResponse,
  period: string,
  fallbackMedicine?: MedicinePriceChangesFallback,
): MedicinePriceChanges {
  const series = (dailyPrices.dates ?? []).map((date, index) => ({
    date,
    avgPrice: toTiyin(dailyPrices.avg?.[index]) ?? 0,
    minPrice: toTiyin(dailyPrices.min?.[index]) ?? 0,
    maxPrice: toTiyin(dailyPrices.max?.[index]) ?? 0,
    samples: dailyPrices.offers?.[index] ?? 0,
  }));

  const firstPoint = series[0] ?? null;
  const lastPoint = series.at(-1) ?? null;
  const startPrice = firstPoint?.avgPrice ?? null;
  const endPrice = lastPoint?.avgPrice ?? null;
  const change = startPrice != null && endPrice != null ? endPrice - startPrice : null;
  const changePercent = change != null && startPrice ? roundToTwo((change / startPrice) * 100) : null;

  return {
    medicine: {
      id: medicineId,
      name: fallbackMedicine?.name ?? `Medicine #${medicineId}`,
      ...fallbackMedicine,
    },
    period: parsePeriod(period),
    summary: {
      startPrice,
      endPrice,
      change,
      changePercent,
      minPrice: series.length ? Math.min(...series.map((point) => point.minPrice)) : null,
      maxPrice: series.length ? Math.max(...series.map((point) => point.maxPrice)) : null,
      samples: series.reduce((sum, point) => sum + point.samples, 0),
    },
    series,
  };
}

/**
 * Normalizes Typesense `highlights` into the canonical
 * `{ trade_name, name }` shape the UI expects.
 *
 * The backend attempts to remap Typesense's internal indexed field names
 * (`trade_name_search`, `name_search`) into display names, but the endpoint
 * can also pass through the raw Typesense v26+ array format
 * (`[{ field, snippet }]`) or the legacy nested-object format
 * (`{ trade_name_search: { snippet } }`). Normalize all of them here so the
 * consuming components only need to read `highlights.trade_name` and
 * `highlights.name`.
 */
export function normalizeMedicineHighlights(raw: unknown): Partial<Record<string, string>> | null {
  if (raw == null) return null;

  const FIELD_MAP: Record<string, string> = {
    trade_name_search: "trade_name",
    name_search: "name",
  };
  const remap = (field: string) => FIELD_MAP[field] ?? field;

  const result: Record<string, string> = {};

  const setSnippet = (field: unknown, snippet: unknown) => {
    if (typeof field !== "string" || typeof snippet !== "string" || snippet.length === 0) return;
    const key = remap(field);
    // Prefer the first snippet we see per field (matches backend precedence).
    if (result[key] == null) result[key] = snippet;
  };

  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (!entry || typeof entry !== "object") continue;
      const field = (entry as { field?: unknown }).field;
      const snippet = (entry as { snippet?: unknown }).snippet ?? (entry as { value?: unknown }).value;
      setSnippet(field, snippet);
    }
  } else if (typeof raw === "object") {
    for (const [field, value] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof value === "string") {
        setSnippet(field, value);
      } else if (value && typeof value === "object") {
        const snippet = (value as { snippet?: unknown }).snippet;
        setSnippet(field, snippet);
      }
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

export function normalizeMedicine(medicine: Medicine): Medicine {
  return {
    ...medicine,
    min_price: normalizeTiyinPrice(medicine.min_price),
    max_price: normalizeTiyinPrice(medicine.max_price),
    wholesale_price: normalizeTiyinPrice(medicine.wholesale_price),
    wholesale_price_min: normalizeTiyinPrice(medicine.wholesale_price_min),
    wholesale_price_max: normalizeTiyinPrice(medicine.wholesale_price_max),
    price: normalizeTiyinPrice(medicine.price),
    minimal_price: normalizeTiyinPrice(medicine.minimal_price),
    lowest_offer_price: normalizeTiyinPrice(medicine.lowest_offer_price),
    lowest_promo_price: normalizeTiyinPrice(medicine.lowest_promo_price),
    highlights: normalizeMedicineHighlights(medicine.highlights),
  };
}

export function normalizeMedicines(medicines: Medicine[]) {
  return medicines.map(normalizeMedicine);
}

export function normalizeMedicinePriceChanges(priceChanges: MedicinePriceChanges): MedicinePriceChanges {
  return {
    ...priceChanges,
    medicine: normalizeMedicine(priceChanges.medicine),
    summary: {
      ...priceChanges.summary,
      startPrice: normalizeTiyinPrice(priceChanges.summary.startPrice),
      endPrice: normalizeTiyinPrice(priceChanges.summary.endPrice),
      change: normalizeTiyinPrice(priceChanges.summary.change),
      minPrice: normalizeTiyinPrice(priceChanges.summary.minPrice),
      maxPrice: normalizeTiyinPrice(priceChanges.summary.maxPrice),
    },
    series: priceChanges.series.map((point) => ({
      ...point,
      avgPrice: normalizeTiyinPrice(point.avgPrice) ?? 0,
      minPrice: normalizeTiyinPrice(point.minPrice) ?? 0,
      maxPrice: normalizeTiyinPrice(point.maxPrice) ?? 0,
    })),
  };
}

export async function getMedicinesByIds(ids: Array<string | number>) {
  if (!ids.length) return [];
  try {
    const response = await http.post<MedicineFindResponse>("/analytics/medicines/by-ids", { ids });
    return normalizeMedicines(response.data?.data ?? []);
  } catch (error) {
    throw toApiError(error, "Unable to load medicines by ids.");
  }
}

export async function findMedicines(params: MedicineFindParams) {
  const searchParams = new URLSearchParams({ q: params.name });
  if (params.vendorIds?.length) {
    searchParams.set("vendor_ids", params.vendorIds.join(","));
  }
  if (params.region) searchParams.set("region", params.region);
  if (params.dosageForm) searchParams.set("dosage_form", params.dosageForm);
  if (params.strength) searchParams.set("strength", params.strength);
  if (params.volume) searchParams.set("volume", params.volume);
  if (params.withOfferCounts) searchParams.set("with_offer_counts", "true");
  if (params.withLowestPrices) searchParams.set("with_lowest_prices", "true");

  const response = await fetchJsonApi<MedicineFindResponse>(`/analytics/medicines?${searchParams.toString()}`);

  return {
    ...response,
    data: normalizeMedicines(response.data),
  };
}

function normalizeMedicineRecommendationPayload(params: MedicineRecommendationRequest): MedicineRecommendationRequest {
  const payload: MedicineRecommendationRequest = {
    query: params.query.trim(),
    limit: Math.min(Math.max(Math.trunc(params.limit ?? 10), 1), 50),
  };

  if (params.region?.trim()) payload.region = params.region.trim();
  if (typeof params.age_years === "number" && Number.isFinite(params.age_years)) {
    payload.age_years = Math.max(0, Math.trunc(params.age_years));
  }
  if (params.sex?.trim()) payload.sex = params.sex.trim();
  if (params.pregnancy_lactation?.trim()) payload.pregnancy_lactation = params.pregnancy_lactation.trim();
  if (params.prescription?.trim()) payload.prescription = params.prescription.trim();
  if (typeof params.group_variants === "boolean") payload.group_variants = params.group_variants;
  if (typeof params.with_availability === "boolean") payload.with_availability = params.with_availability;

  return payload;
}

export async function fetchMedicineRecommendations(params: MedicineRecommendationRequest) {
  try {
    const response = await http.post<MedicineRecommendationResponse>(
      "/analytics/medicines/recommendations",
      normalizeMedicineRecommendationPayload(params),
    );
    return {
      ...response.data,
      data: {
        recommendations: response.data.data?.recommendations ?? [],
        planner: response.data.data?.planner ?? null,
        disclaimer: response.data.data?.disclaimer ?? null,
        total_candidates: response.data.data?.total_candidates ?? null,
      },
    };
  } catch (error) {
    throw toApiError(error, "Unable to load medicine recommendations.");
  }
}

export async function getMedicinePriceChanges(
  medicineId: string | number,
  period = "30d",
  fallbackMedicine?: MedicinePriceChangesFallback,
) {
  const response = await fetchJsonApi<DailyPricesResponse>(
    `/analytics/daily-prices?medicine_id=${encodeURIComponent(medicineId)}`,
  );

  return normalizeMedicinePriceChanges(
    buildMedicinePriceChangesFromDailyPrices(medicineId, response, period, fallbackMedicine),
  );
}

export async function getRetailDailyPrices(
  medicineId: string | number,
  options: { startDate?: string | null; endDate?: string | null } = {},
) {
  const params = new URLSearchParams({ medicine_id: String(medicineId) });
  if (options.startDate) params.set("start_date", options.startDate);
  if (options.endDate) params.set("end_date", options.endDate);
  return fetchJsonApi<ColumnarPriceHistory>(`/analytics/daily-prices?${params.toString()}`);
}

export async function getWholesaleDailyPrices(
  medicineId: string | number,
  options: { region?: string | null; startDate?: string | null; endDate?: string | null } = {},
) {
  const params = new URLSearchParams({ medicine_id: String(medicineId) });
  if (options.region) params.set("region", options.region);
  if (options.startDate) params.set("start_date", options.startDate);
  if (options.endDate) params.set("end_date", options.endDate);
  return fetchJsonApi<ColumnarPriceHistory>(`/analytics/wholesale-daily-prices?${params.toString()}`);
}

export async function getExpiryChanges(medicineId: string | number) {
  return fetchJsonApi<ExpiryChangesHistory>(`/analytics/expiry-changes?medicine_id=${encodeURIComponent(medicineId)}`);
}

export type WholesaleDateOffer = {
  organization_id: string;
  name?: string | null;
  raw_name?: string | null;
  vendor_name?: string | null;
  medicine_id?: string | null;
  price: number;
  issue_date: string;
  expiry_date?: string | null;
  offer_end_date?: string | null;
  price_list_file_id: string | null;
  row_index: number | null;
};

export type WholesaleDateOffersResponse = {
  offers: WholesaleDateOffer[];
  organizations: Record<string, string>;
};

export async function fetchWholesaleOffersForDateRange(
  medicineId: string,
  startDate: string,
  endDate: string,
  options: { region?: string | null } = {},
) {
  try {
    const body: { medicine_id: string; start_date: string; end_date: string; region?: string } = {
      medicine_id: medicineId,
      start_date: startDate,
      end_date: endDate,
    };
    if (options.region) body.region = options.region;
    const response = await http.post<{ data: WholesaleDateOffersResponse }>(
      "/app/wholesale-offers/analyze/date-offers",
      body,
    );
    return response.data.data;
  } catch (error) {
    throw toApiError(error, "Unable to load wholesale offers.");
  }
}

export type RetailDateOffer = {
  pharmacy_id: string;
  price: number;
  count: number | null;
  date: string;
  last_crawled_at: string | null;
};

export type RetailDatePharmacyInfo = {
  id: string;
  name: string;
  address: string | null;
  organization_id: string | null;
};

export type RetailDateOffersResponse = {
  offers: RetailDateOffer[];
  pharmacies: Record<string, RetailDatePharmacyInfo>;
};

export async function fetchRetailOffersForDate(medicineId: string, date: string) {
  try {
    const response = await http.post<{ data: RetailDateOffersResponse }>("/app/retail-prices/analyze/date-offers", {
      medicine_id: medicineId,
      start_date: date,
      end_date: date,
    });
    return response.data.data;
  } catch (error) {
    throw toApiError(error, "Unable to load retail offers for date.");
  }
}

export type MedicineVendorInfo = {
  id: string;
  name: string;
  country?: string | null;
  logo_sm?: string | null;
  logo_big?: string | null;
};

export type MedicineDetailIcd10Code = {
  code: string;
  label?: string | null;
};

export type MedicineDetailSourceMetadata = {
  canonical_url?: string | null;
  crawled_at?: string | null;
  article_body_present?: boolean | null;
  [key: string]: unknown;
};

export type MedicineDetails = Medicine & {
  medicine_id: string;
  certified_medicine_id: number | null;
  image_sm?: string | null;
  image_bg?: string | null;
  source?: string | null;
  source_slug?: string | null;
  source_url?: string | null;
  source_updated_at?: string | null;
  display_name?: string | null;
  trade_name_latin?: string | null;
  dosage_form_text?: string | null;
  is_vital_essential?: boolean | null;
  active_substance_ru?: string | null;
  active_substance_en?: string | null;
  manufacturer_name?: string | null;
  contact_organization_name?: string | null;
  clinical_pharmacological_group?: string | null;
  pharmacotherapeutic_group?: string | null;
  icd10_codes?: MedicineDetailIcd10Code[] | null;
  indications?: string | null;
  contraindications?: string | null;
  dosage_instructions?: string | null;
  side_effects?: string | null;
  storage_conditions?: string | null;
  shelf_life_months?: number | null;
  composition?: string | null;
  pharmacological_action?: string | null;
  pharmacokinetics?: string | null;
  pregnancy_lactation?: string | null;
  hepatic_impairment?: string | null;
  renal_impairment?: string | null;
  pediatric_use?: string | null;
  geriatric_use?: string | null;
  special_instructions?: string | null;
  overdose?: string | null;
  drug_interactions?: string | null;
  pharmacy_release_terms?: string | null;
  raw_sections?: unknown;
  source_metadata?: MedicineDetailSourceMetadata | null;
  vendor?: MedicineVendorInfo | null;
};

export async function getMedicineDetails(medicineId: string | number) {
  const response = await fetchJsonApi<{ data: MedicineDetails }>(
    `/analytics/medicines/${encodeURIComponent(medicineId)}/details`,
  );
  return response.data;
}

export type MedicineIcdCode = {
  code: string;
  description?: string | null;
  parent_code?: string | null;
};

export async function getMedicineIcdCodes(medicineId: string | number): Promise<MedicineIcdCode[]> {
  const response = await fetchJsonApi<{ data: MedicineIcdCode[] }>(
    `/analytics/medicines/${encodeURIComponent(medicineId)}/icd-codes`,
  );
  return response.data ?? [];
}

export type MedicineVariation = {
  id: string;
  international_name_ru?: string | null;
  international_name?: string | null;
  trade_name?: string | null;
  dosage_form?: string | null;
  product_type?: string | null;
  strength?: string | null;
  volume?: string | null;
  container_type?: string | null;
  pack?: number | null;
  vendor?: MedicineVendorInfo | null;
};

export async function getMedicineVariations(medicineId: string | number): Promise<MedicineVariation[]> {
  const { data } = await http.get<{ data: MedicineVariation[] }>(
    `/analytics/medicines/${encodeURIComponent(medicineId)}/variations`,
  );
  return data.data ?? [];
}

export async function fetchMedicineAnalogues(medicineId: string | number) {
  const response = await fetchJsonApi<{ data: MedicineDetails[] }>(
    `/analytics/medicines/${encodeURIComponent(medicineId)}/analogues`,
  );
  return (response.data ?? []).map((item) => normalizeMedicine(item) as MedicineDetails);
}

export type GetLatestWholesaleOffersOptions = {
  organization_ids?: string[];
  exclude_organization_ids?: string[];
  has_promotion?: boolean;
  region?: string | null;
  payment_percentage?: number | null;
};

export async function getLatestWholesaleOffers(
  medicineIds: Array<string | number>,
  options: GetLatestWholesaleOffersOptions = {},
) {
  try {
    const body: Record<string, unknown> = { medicine_ids: medicineIds };
    if (options.organization_ids?.length) body.organization_ids = options.organization_ids;
    if (options.exclude_organization_ids?.length) body.exclude_organization_ids = options.exclude_organization_ids;
    if (options.has_promotion !== undefined) body.has_promotion = options.has_promotion;
    if (options.region) body.region = options.region;
    if (options.payment_percentage != null) body.payment_percentage = options.payment_percentage;
    const response = await http.post<{ data: LatestWholesaleOffersResponse }>(
      "/app/wholesale-offers/latest-offers",
      body,
    );
    return response.data.data;
  } catch (error) {
    throw toApiError(error, "Unable to load latest wholesale offers.");
  }
}

export type RetailSalesScope = {
  type: string;
  medicine_id: string;
  medicine_ids: string[];
};

export type RetailSalesItem = {
  date: string;
  sales_count: number;
  gain_count: number;
  net_change: number;
  pharmacy_count: number;
  sales_delta: number;
  gain_delta: number;
  net_delta: number;
};

export type RetailSalesSummary = {
  days: number;
  included_days: number;
  excluded_days: number;
  total_sales_count: number;
  average_daily_sales_count: number;
  median_daily_sales_count: number;
  total_gain_count: number;
  average_daily_gain_count: number;
  median_daily_gain_count: number;
  total_net_change: number;
  average_daily_net_change: number;
  median_daily_net_change: number;
  sales_outlier_bounds?: { lower: number; upper: number };
  gain_outlier_bounds?: { lower: number; upper: number };
  net_outlier_bounds?: { lower: number; upper: number };
  outlier_method?: string;
};

export type RetailSalesMember = {
  medicine_id: string;
  name: string;
  atc_code?: string | null;
  total_sales_count: number;
  average_daily_sales_count: number;
  median_daily_sales_count: number;
  total_gain_count: number;
  total_net_change: number;
};

export type RetailSalesPeerComparison = {
  target_total_sales_count: number;
  peer_average_total_sales_count: number;
  peer_median_total_sales_count: number;
  target_average_daily_sales_count: number;
  peer_average_daily_sales_count: number;
  peer_median_daily_sales_count: number;
  total_sales_difference_vs_peer_median: number;
  average_daily_sales_difference_vs_peer_median: number;
  rank_by_total_sales: number;
  cohort_size_including_target: number;
  percentile_by_total_sales: number;
  better_than_peers_count: number;
  worse_or_equal_peers_count: number;
};

export type RetailSalesPeerDailyBenchmark = {
  dates: string[];
  avg_sales_count: number[];
  median_sales_count: number[];
  avg_gain_count: number[];
  median_gain_count: number[];
};

export type RetailSalesTopPeer = {
  medicine_id: string;
  name: string;
  total_sales_count: number;
  average_daily_sales_count: number;
  median_daily_sales_count: number;
  total_gain_count: number;
  total_net_change: number;
};

export type RetailSalesBenchmarks = {
  target_medicine?: { id: string; name: string; atc_code?: string | null };
  same_atc_code?: {
    label: string;
    peer_count: number;
    peer_medicine_ids: string[];
    comparison: RetailSalesPeerComparison;
    peer_daily_benchmark: RetailSalesPeerDailyBenchmark;
    top_peers: RetailSalesTopPeer[];
  };
};

export type RetailSalesAnalytics = {
  scope: RetailSalesScope;
  items: RetailSalesItem[];
  summary: RetailSalesSummary;
  members: RetailSalesMember[];
  benchmarks?: RetailSalesBenchmarks;
};

type RetailSalesAnalyticsResponse = {
  data: RetailSalesAnalytics;
};

export async function fetchMedicineAutocomplete(query: string): Promise<string | null> {
  const response = await http.get<{ suggestion: string | null }>("/analytics/medicines/autocomplete", {
    params: { q: query },
  });
  return response.data.suggestion;
}

export async function fetchRetailSalesAnalytics(medicineId: string): Promise<RetailSalesAnalytics> {
  try {
    const response = await http.post<RetailSalesAnalyticsResponse>("/app/retail-prices/sales", {
      medicine_id: medicineId,
    });
    return response.data.data;
  } catch (error) {
    throw toApiError(error, "Unable to load retail sales analytics.");
  }
}

// ── Trade-name search ──────────────────────────────────────────────────

export type TradeNameVariationFilterOption<V = string> = {
  value: V;
  offer_count: number;
};

export type TradeNameVariationFilters = {
  dosage_form: TradeNameVariationFilterOption<string>[];
  product_type: TradeNameVariationFilterOption<string>[];
  strength: TradeNameVariationFilterOption<string>[];
  volume: TradeNameVariationFilterOption<string>[];
  container_type: TradeNameVariationFilterOption<string>[];
  pack: TradeNameVariationFilterOption<number>[];
};

export type TradeNameMedicinesResponse = {
  medicines: Medicine[];
  filters: TradeNameVariationFilters;
};

type OneOrMany<T> = T | T[];

function serializeFilterValue<T extends string | number>(
  value: OneOrMany<T> | null | undefined,
): T | string | undefined {
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(",") : undefined;
  }
  if (typeof value === "string") return value || undefined;
  return value ?? undefined;
}

export async function getMedicinesByTradeName(
  tradeName: string | null,
  options: {
    stringMatch?: string | null;
    medicineIds?: string[] | null;
    withAnalogous?: boolean | null;
    region?: string | null;
    organizationId?: string | null;
    dosageForm?: OneOrMany<string> | null;
    productType?: OneOrMany<string> | null;
    strength?: OneOrMany<string> | null;
    volume?: OneOrMany<string> | null;
    containerType?: OneOrMany<string> | null;
    pack?: OneOrMany<number> | null;
    hasPromotion?: boolean;
    paymentPercentage?: number | null;
  } = {},
): Promise<TradeNameMedicinesResponse> {
  const { data } = await http.get<{ data: TradeNameMedicinesResponse }>("/analytics/medicines/by-trade-name", {
    params: {
      trade_name: tradeName || undefined,
      string_match: options.stringMatch || undefined,
      medicine_ids: options.medicineIds?.length ? options.medicineIds.join(",") : undefined,
      with_analogous: options.withAnalogous ? true : undefined,
      region: options.region || undefined,
      organization_id: options.organizationId || undefined,
      dosage_form: serializeFilterValue(options.dosageForm),
      product_type: serializeFilterValue(options.productType),
      strength: serializeFilterValue(options.strength),
      volume: serializeFilterValue(options.volume),
      container_type: serializeFilterValue(options.containerType),
      pack: serializeFilterValue(options.pack),
      has_promotion: options.hasPromotion || undefined,
      payment_percentage: options.paymentPercentage ?? undefined,
    },
  });
  return data.data;
}
